import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ChallengeState } from '@/app/shared/interfaces';

@Injectable({
    providedIn: 'root',
})
export class ChallengeStorageService {
    private readonly AUTH_CHALLENGE_TOKEN_KEY = 'authChallengeToken';
    private readonly CHALLENGE_DATA_KEY = 'challengeData';
    private challengeSubject = new BehaviorSubject<ChallengeState | null>(this.getChallengeFromStorage());

    constructor() {
        this.loadChallengeFromStorage();
    }

    getAuthChallengeToken(): string | null {
        return sessionStorage.getItem(this.AUTH_CHALLENGE_TOKEN_KEY);
    }

    getChallengeData(): Partial<ChallengeState> | null {
        const challengeData = sessionStorage.getItem(this.CHALLENGE_DATA_KEY);
        return challengeData ? JSON.parse(challengeData) : null;
    }

    saveChallenge(challenge: ChallengeState | null): void {
        if (challenge && challenge.authChallengeToken) {
            sessionStorage.setItem(this.AUTH_CHALLENGE_TOKEN_KEY, challenge.authChallengeToken);
            const challengeData = { ...challenge };
            delete challengeData.authChallengeToken;
            sessionStorage.setItem(this.CHALLENGE_DATA_KEY, JSON.stringify(challengeData));

            this.challengeSubject.next(challenge);
        } else {
            this.clearChallenge();
        }
    }

    clearChallenge(): void {
        sessionStorage.removeItem(this.AUTH_CHALLENGE_TOKEN_KEY);
        sessionStorage.removeItem(this.CHALLENGE_DATA_KEY);
    }

    isTokenExpired(): boolean {
        const token = this.getAuthChallengeToken();
        if (!token) return true;
        try {
            const payload = this.parseJwt(token);
            if (!payload || !payload.exp) return true;
            const currentTime = Math.floor(Date.now() / 1000);
            return payload.exp < currentTime;
        } catch (e) {
            return true;
        }
    }

    private parseJwt(token: string): any {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                    .join(''),
            );
            return JSON.parse(jsonPayload);
        } catch (e) {
            return null;
        }
    }

    public getChallengeFromStorage(): ChallengeState | null {
        const token = this.getAuthChallengeToken();
        const data = this.getChallengeData();

        if (!token || this.isTokenExpired()) {
            this.clearChallenge();
            return null;
        }

        if (token && data) {
            return {
                authChallengeToken: token,
                ...data,
            };
        }

        return null;
    }

    private loadChallengeFromStorage(): void {
        const challenge = this.getChallengeFromStorage();
        this.challengeSubject.next(challenge);
    }
}
